import { css, html, nothing } from "lit";
import { query, queryAssignedElements } from "lit/decorators.js";
import { autoUpdate, offset, flip, shift, hide, computePosition } from "@floating-ui/dom";
import { defineElement } from "../../../decorators/defineElement.js";
import { BaseComponent } from "../../BaseComponent.js";
import { prop } from "../../../decorators/prop/index.js";
import { getDropdownClasses } from "./utils.js";
import { getArrowClasses, placements } from "./dropdownArrow.js";
import { classNames } from "../../../styles/index.js";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
let DsfDropdown = class extends BaseComponent {
  constructor() {
    super(...arguments);
    this.dsf_open = false;
    this.dsf_placement = "bottom-start";
    this.dsf_offset = 0;
    this.dsf_flip = false;
    this.dsf_shift = false;
    this.dsf_padding = 8;
    this.dsf_hide = false;
    this.dsf_same_width = false;
    this.dsf_hover_bridge = false;
    this.updateHoverBridge = (placement) => {
      const anchor = this.anchorElement;
      const dropdown = this.dropdownElement;
      if (!anchor || !dropdown || !this.dsf_hover_bridge) {
        return;
      }
      const anchorRect = anchor.getBoundingClientRect();
      const popupRect = dropdown.getBoundingClientRect();
      const isVertical = placement.includes("top") || placement.includes("bottom");
      let topLeftX = 0;
      let topLeftY = 0;
      let topRightX = 0;
      let topRightY = 0;
      let bottomLeftX = 0;
      let bottomLeftY = 0;
      let bottomRightX = 0;
      let bottomRightY = 0;
      if (isVertical) {
        if (anchorRect.top < popupRect.top) {
          topLeftX = anchorRect.left;
          topLeftY = anchorRect.bottom;
          topRightX = anchorRect.right;
          topRightY = anchorRect.bottom;
          bottomLeftX = popupRect.left;
          bottomLeftY = popupRect.top;
          bottomRightX = popupRect.right;
          bottomRightY = popupRect.top;
        } else {
          topLeftX = popupRect.left;
          topLeftY = popupRect.bottom;
          topRightX = popupRect.right;
          topRightY = popupRect.bottom;
          bottomLeftX = anchorRect.left;
          bottomLeftY = anchorRect.top;
          bottomRightX = anchorRect.right;
          bottomRightY = anchorRect.top;
        }
      } else {
        if (anchorRect.left < popupRect.left) {
          topLeftX = anchorRect.right;
          topLeftY = anchorRect.top;
          topRightX = popupRect.left;
          topRightY = popupRect.top;
          bottomLeftX = anchorRect.right;
          bottomLeftY = anchorRect.bottom;
          bottomRightX = popupRect.left;
          bottomRightY = popupRect.bottom;
        } else {
          topLeftX = popupRect.right;
          topLeftY = popupRect.top;
          topRightX = anchorRect.left;
          topRightY = anchorRect.top;
          bottomLeftX = popupRect.right;
          bottomLeftY = popupRect.bottom;
          bottomRightX = anchorRect.left;
          bottomRightY = anchorRect.bottom;
        }
      }
      this.style.setProperty("--hover-bridge-top-left-x", `${topLeftX}px`);
      this.style.setProperty("--hover-bridge-top-left-y", `${topLeftY}px`);
      this.style.setProperty("--hover-bridge-top-right-x", `${topRightX}px`);
      this.style.setProperty("--hover-bridge-top-right-y", `${topRightY}px`);
      this.style.setProperty("--hover-bridge-bottom-left-x", `${bottomLeftX}px`);
      this.style.setProperty("--hover-bridge-bottom-left-y", `${bottomLeftY}px`);
      this.style.setProperty("--hover-bridge-bottom-right-x", `${bottomRightX}px`);
      this.style.setProperty("--hover-bridge-bottom-right-y", `${bottomRightY}px`);
    };
  }
  get anchorElement() {
    if (!this.anchorElements.length) {
      return null;
    }
    return this.anchorElements[0];
  }
  async connectedCallback() {
    var _a;
    super.connectedCallback();
    this.updatePosition = this.updatePosition.bind(this);
    await this.updateComplete;
    this.restartAutoUpdate();
    const slot = (_a = this.shadowRoot) == null ? void 0 : _a.querySelector("slot:not([name])");
    this.arrowElement = slot.assignedElements().find((el) => this.dsf_arrow && el.matches(this.dsf_arrow));
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.stopAutoUpdate();
  }
  renderHoverBridge() {
    return this.dsf_hover_bridge && this.dsf_open ? html` <div class="dropdown-hover-bridge fixed" part="hover-bridge"></div> ` : nothing;
  }
  restartAutoUpdate() {
    this.stopAutoUpdate();
    if (!this.dsf_open || !this.anchorElement) {
      return;
    }
    this.cleanup = autoUpdate(this.anchorElement, this.dropdownElement, this.updatePosition);
  }
  stopAutoUpdate() {
    if (this.cleanup) {
      this.cleanup();
      this.cleanup = void 0;
    }
  }
  async updatePosition() {
    const anchor = this.anchorElement;
    const dropdown = this.dropdownElement;
    if (!anchor) {
      return;
    }
    const middleware = [offset(this.dsf_offset)];
    if (this.dsf_flip) {
      middleware.push(flip({ padding: this.dsf_padding }));
    }
    if (this.dsf_shift) {
      middleware.push(shift({ padding: this.dsf_padding }));
    }
    if (this.dsf_hide) {
      middleware.push(hide());
    }
    const strategy = document.body.classList.contains(classNames.disableScroll) ? "fixed" : "absolute";
    const { x, y, middlewareData, placement } = await computePosition(anchor, dropdown, {
      strategy,
      placement: this.dsf_placement,
      middleware
    });
    Object.assign(dropdown.style, {
      left: `${x}px`,
      top: `${y}px`
    });
    if (middlewareData.hide) {
      Object.assign(dropdown.style, {
        visibility: middlewareData.hide.referenceHidden ? "hidden" : ""
      });
    }
    if (this.dsf_same_width) {
      Object.assign(dropdown.style, {
        width: `${anchor.offsetWidth}px`
      });
    }
    if (this.dsf_arrow && this.arrowElement) {
      this.positionArrowElement(this.arrowElement, anchor, placement, middlewareData);
    }
    if (this.dsf_hover_bridge) {
      this.updateHoverBridge(placement);
    }
  }
  positionArrowElement(arrowElement, anchor, placement, middlewareData) {
    var _a, _b;
    const arrowClasses = getArrowClasses(placement);
    arrowElement.className = arrowClasses;
    const arrowWidth = arrowElement.offsetWidth;
    const arrowHeight = arrowElement.offsetHeight;
    const arrowVerticalCenter = arrowElement.offsetHeight / 2;
    const arrowHorizontalCenter = arrowElement.offsetWidth / 2;
    const anchorVerticalCenter = anchor.offsetHeight / 2;
    const anchorHorizontalCenter = anchor.offsetWidth / 2;
    let arrowX;
    let arrowY;
    switch (placement) {
      case "top":
      case "bottom":
        arrowX = (this.dropdownElement.offsetWidth - arrowWidth) / 2;
        break;
      case "right":
      case "left":
        arrowY = (this.dropdownElement.offsetHeight - arrowHeight) / 2;
        break;
      case "top-start":
      case "bottom-start":
        arrowX = Math.min(Math.abs(anchorHorizontalCenter - arrowHorizontalCenter), arrowWidth);
        break;
      case "top-end":
      case "bottom-end":
        arrowX = this.dropdownElement.offsetWidth - arrowElement.offsetWidth - Math.min(Math.abs(anchorHorizontalCenter - arrowHorizontalCenter), arrowWidth);
        break;
      case "left-start":
      case "right-start":
        arrowY = Math.min(Math.abs(anchorVerticalCenter - arrowVerticalCenter), arrowHeight);
        break;
      case "left-end":
      case "right-end":
        arrowY = this.dropdownElement.offsetHeight - arrowElement.offsetHeight - Math.min(Math.abs(anchorVerticalCenter - arrowVerticalCenter), arrowHeight);
        break;
    }
    if (((_a = middlewareData.shift) == null ? void 0 : _a.x) && arrowX) {
      arrowX -= middlewareData.shift.x;
    }
    if (((_b = middlewareData.shift) == null ? void 0 : _b.y) && arrowY) {
      arrowY -= middlewareData.shift.y;
    }
    Object.assign(arrowElement.style, {
      left: arrowX !== void 0 ? `${arrowX}px` : "",
      top: arrowY !== void 0 ? `${arrowY}px` : ""
    });
    if (!arrowY) {
      if (placement.startsWith("top")) {
        arrowElement.style.bottom = `-${arrowHeight - 1}px`;
      } else if (placement.startsWith("bottom")) {
        arrowElement.style.top = `-${arrowHeight - 1}px`;
      }
    }
    if (!arrowX) {
      if (placement.startsWith("right")) {
        arrowElement.style.left = `-${arrowWidth - 1}px`;
      } else if (placement.startsWith("left")) {
        arrowElement.style.right = `-${arrowWidth - 1}px`;
      }
    }
  }
  handleAnchorUpdate() {
    this.restartAutoUpdate();
  }
  updated(changedProps) {
    super.update(changedProps);
    if (changedProps.has("dsf_open")) {
      if (this.dsf_open) {
        this.restartAutoUpdate();
      } else {
        this.stopAutoUpdate();
      }
    }
    if (this.dsf_open) {
      void this.updatePosition();
    }
  }
  render() {
    const dropdownClasses = getDropdownClasses(this);
    return html`
      <slot name="anchor" @slotchange=${this.handleAnchorUpdate}></slot>
      ${this.renderHoverBridge()}
      <div id="dropdown" part="dropdown" class=${dropdownClasses}>
        <slot></slot>
      </div>
    `;
  }
};
DsfDropdown.styles = [
  ...BaseComponent.styles,
  css`
      :host {
        position: relative;
      }

      .dropdown-hover-bridge {
        z-index: calc(var(--dsf-z-index-dropdown) - 1);
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        clip-path: polygon(
          var(--hover-bridge-top-left-x, 0) var(--hover-bridge-top-left-y, 0),
          var(--hover-bridge-top-right-x, 0) var(--hover-bridge-top-right-y, 0),
          var(--hover-bridge-bottom-right-x, 0) var(--hover-bridge-bottom-right-y, 0),
          var(--hover-bridge-bottom-left-x, 0) var(--hover-bridge-bottom-left-y, 0)
        );
      }
    `
];
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfDropdown.prototype, "dsf_open", 2);
__decorateClass([
  prop({
    type: String,
    reflect: true,
    validator: (value) => placements.includes(value)
  })
], DsfDropdown.prototype, "dsf_placement", 2);
__decorateClass([
  prop({ type: Number, validator: (value) => value >= 0 })
], DsfDropdown.prototype, "dsf_offset", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfDropdown.prototype, "dsf_flip", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfDropdown.prototype, "dsf_shift", 2);
__decorateClass([
  prop({ type: Number, validator: (value) => value >= 0 })
], DsfDropdown.prototype, "dsf_padding", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfDropdown.prototype, "dsf_hide", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfDropdown.prototype, "dsf_same_width", 2);
__decorateClass([
  prop({ type: String })
], DsfDropdown.prototype, "dsf_arrow", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfDropdown.prototype, "dsf_hover_bridge", 2);
__decorateClass([
  query("#dropdown")
], DsfDropdown.prototype, "dropdownElement", 2);
__decorateClass([
  queryAssignedElements({ selector: '[slot="anchor"]', slot: "anchor" })
], DsfDropdown.prototype, "anchorElements", 2);
DsfDropdown = __decorateClass([
  defineElement("dsf-dropdown")
], DsfDropdown);
export {
  DsfDropdown
};
